<template>
  <v-container fluid>
    <div class="d-flex justify-space-between align-center">
      <h2 class="mb-0">
        {{ $t('sustainability_dashboard_ui.staging_reefer_activity') }}
      </h2>
      <sustainability-settings/>
    </div>
    <div class="mb-2 d-flex justify-content-between">
      <h4>{{ selectedCustomer.name }} <span class="small ml-2 text-muted">({{ this.summaryString }})</span></h4>
      <v-btn size="sm" @click="backToDashboard()">
        <v-icon>mdi-arrow-left</v-icon>
        {{ $t('sustainability_dashboard_ui.back_to_dashboard') }}
      </v-btn>
    </div>
    <staging-metrics-summary :metricsData="metricsData" :loading="loadingMetrics"/>
    <hr>

    <v-sheet v-if="metricsData" outlined rounded class="px-4 py-2">
      <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
      <v-row dense>
        <v-col lg="4" md="6" cols="12">
          <v-text-field
            v-model="searchCriteria.name"
            @keyup.enter.native="initiateSearch()"
            :placeholder="$t('route_name')"
            prepend-inner-icon="mdi-road-variant"/>
        </v-col>
        <v-col lg="4" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('vehicle', 2)"
            v-model="searchCriteria.vehicles"
            :items="vehicleNames"
            prepend-inner-icon="mdi-truck"/>
        </v-col>
        <v-col lg="4" md="6" cols="12">
          <v-text-field
            v-model="searchCriteria.overAverage"
            @keyup.enter.native="initiateSearch()"
            :placeholder="$t('over_average')"
            prepend-inner-icon="mdi-percent"/>
        </v-col>
      </v-row>
      <div class="d-flex justify-end mt-1 mb-2">
        <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
        <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
          <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>
    </v-sheet>

    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
    </div>
    <!-- Data Table -->
    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="vf-table mt-2"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">

        <template v-slot:header="props">
          <thead>
          <tr>
            <th colspan="2"><span class="sr-only">Vehicle and Route</span></th>
            <th colspan="3" class="text-center">{{ $t('route_staging_interval') }}</th>
            <th colspan="3" class="text-center">{{ $t('potential_savings') }}</th>
          </tr>
          </thead>
        </template>

        <template v-slot:item.vehicle="row">
          <router-link v-if="row.item.vehicle != null" :to="`/vehicle/${row.item.vehicle.id}`"><v-icon>mdi-truck</v-icon>{{row.item.vehicle.name}}</router-link>
          <router-link v-else class="font-italic" target="_blank" :href="`https://${zendeskDomain}/hc/en-us/articles/360042724133-Vehicle-Cannot-Be-Displayed`">{{ $t('cannot_be_displayed') }}</router-link>
        </template>

        <template v-slot:item.name="row">
          <router-link :to="`/route/${row.item.id}`"><v-icon>mdi-road</v-icon>{{row.item.name}}</router-link>
        </template>

        <template v-slot:item.start="row">
          <if-set :value="row.value">
            <template v-slot="scope">
              <multi-time-display :time="scope.value" />
            </template>
          </if-set>
        </template>
        <template v-slot:item.staging_duration="row">
          <if-set v-if="!row.item.isHeader" :value="row.value">
            {{ row.value | humanizeDuration({ numberOfSignificantParts: 2, separator: ', ' }) }}
          </if-set>
        </template>

        <template v-slot:item.diesel_used="row">
          <if-set :value="row.value">
            <template>{{ formatNumber(row.value, 1) }}</template>
          </if-set>
        </template>

        <template v-slot:item.over_target="row">
          <if-set :value="row.value">
            <template>{{ formatPercentage(row.value, 1) }}</template>
          </if-set>
        </template>

        <template v-slot:item.currency_savings="row">
          <if-set :value="row.value">
            <template>{{ hideNegativeSavings(formatCurrency(row.value, currencySymbol), row) }}</template>
          </if-set>
        </template>

        <template v-slot:item.fuel_quantity_savings="row">
          <if-set :value="row.value">
            <template>{{ hideNegativeSavings(formatNumber(row.value, 1), row) }}</template>
          </if-set>
        </template>

        <template v-slot:item.co2_savings="row">
          <if-set :value="row.value">
            <template>{{ hideNegativeSavings(formatNumber(row.value, 1), row) }}</template>
          </if-set>
        </template>
      </v-data-table>
 </div>

  </v-container>
</template>

<script>
import StagingMetricsSummary from '@/components/sustainability/StagingMetricsSummary'
import SustainabilitySettings from '@/components/sustainability/SustainabilitySettings'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import TablePaginationControls from '@/components/shared/TablePaginationControls.vue'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import SustainabilityMixin from '@/components/mixins/SustainabilityMixin'
import MathUtil from '@/util/MathUtil'
import { map, sortBy, round, find, first, values, isEqual } from 'lodash'
import Moment from 'moment-timezone'
import VfAutocomplete from '@/components/shared/VfAutocomplete.vue'

export default {
  name: 'StagingReeferActivityIndex',
  components: {
    VfAutocomplete,
    StagingMetricsSummary,
    SustainabilitySettings,
    TablePaginationControls,
    MultiTimeDisplay
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin,
    ApiUtilitiesMixin,
    DateTimeUtilsMixin,
    SustainabilityMixin
  ],
  data () {
    return {
      model: 'SustainabilityRoute',
      metricsData: null,
      vehicles: [],
      searchCriteria: this.getDefaultSearchCriteria(),
      loadingMetrics: false,
      totalDieselUsed: 0
    }
  },
  methods: {
    /** small func to navigate back to dashboard, using a 'to' attribute makes the button look funny for some reason */
    backToDashboard () {
      this.$router.push('/sustainability/customers/' + this.selectedCustomer.id)
    },
    /**
     * A little trick method to hide certain fields when the savings would be zero or negative (because the route has
     * equal or lower usage compared to the target)
     * @param value
     * @param row
     */
    hideNegativeSavings (value, row) {
      if (row.item.over_target <= 0) {
        return null
      }
      return value
    },
    /**
     * Convenience function to return formatted string of number parameter
     * @param value
     * @param precision
     * @returns {string}
     */
    formatNumber (value, precision) {
      return this.$n(round(value, precision).toFixed(precision))
    },
    /**
     * Convenience function to return formatted string of percentage value
     * @param value
     * @param precision
     * @returns {string|null}
     */
    formatPercentage (value, precision) {
      const formattedValue = this.formatNumber(value, precision)
      if (formattedValue) {
        return formattedValue + '%'
      }
      return null
    },
    /**
     * Sets our data table back to its original state before the user applied filtering and paging changes.
     * @return undefined
     */
    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.remoteData.sortBy = 'over_target'
      this.remoteData.sortDesc = true
      this.initiateSearch()
    },

    /**
     * Returns the default search criteria for this data table
     * @return Object
     */
    getDefaultSearchCriteria () {
      return {
        name: null,
        vehicles: [],
        overTarget: null
      }
    },

    /**
     * Similar to the usual buildSearchCriteria used in our tables, but this will be used to build the params to the
     * request to the staging metrics. We don't use the filter params for this, just the customer id and time frame
     */
    buildMetricsCriteria () {
      const searchCriteria = {
        customer_id: this.selectedCustomer.id,
        gallons_per_hour: this.sustainabilityParams.gallonsPerHour
      }

      if (this.sustainabilityParams.timeRange !== 'custom') {
        searchCriteria.start = this.sustainabilityParams.timeRange
        searchCriteria.end = 'now'
      } else {
        searchCriteria.start = Moment.tz(this.sustainabilityParams.startDate, this.customerTimezone).format()
        searchCriteria.end = Moment.tz(this.sustainabilityParams.endDate, this.customerTimezone).format()
      }

      return searchCriteria
    },

    /**
     * Constructs the current search criteria for the tableProvider request.
     *
     * @return object
     */
    buildSearchCriteria () {
      const searchCriteria = {
        customer_id: this.selectedCustomer.id,
        strategy: 'staging',
        expand: 'vehicle,staging_reefer_activity',
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }

      // Filter by name (if specified)
      if (this.searchCriteria.name != null) {
        searchCriteria.name = this.searchCriteria.name
      }

      // Filter by vehicle (if specified)
      if (this.searchCriteria.vehicles.length > 0) {
        searchCriteria.vehicle_id = map(this.searchCriteria.vehicles, 'value').join(',')
      }

      // Filter by overTarget
      if (typeof this.searchCriteria.overTarget === 'number') {
        // Filter by the amount over, i.e. if the overTarget value is 50, then we divide by 100 to get .5, then add 1 to
        // get 1.5 which we multiply by the target amount, giving us 150% of the target amount.
        const multiplier = this.searchCriteria.overTarget / 100 + 1
        searchCriteria.power_diesel_l_over = round(multiplier * this.metricsData.diesel.power_diesel_target_l, 6)
      }

      if (this.remoteData.sortBy !== null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }

      if (this.sustainabilityParams.timeRange !== 'custom') {
        searchCriteria.start_after = this.sustainabilityParams.timeRange
        searchCriteria.start_before = 'now'
      } else {
        searchCriteria.start_after = Moment.tz(this.sustainabilityParams.startDate, this.customerTimezone).format()
        searchCriteria.start_before = Moment.tz(this.sustainabilityParams.endDate, this.customerTimezone).set({ hour: 23, minute: 59, second: 59 }).format()
      }

      return searchCriteria
    },

    /**
     * Given seconds of power time convert to liters of usage based on customer settng.
     * @param quantity
     * @param unit
     * @param precision
     * @returns number
     */
    convertDieselSecToUsage (seconds, precision) {
      return Math.round(3.78541 * this.sustainabilityParams.gallonsPerHour * (seconds / 3600), precision)
    },

    /**
     * Adapt the remote data into a format suitable for display in the table. This includes adding a couple new fields
     * generated from the existing data.
     *
     * @param items
     */
    transformRemoteData (items) {
      return map(items, item => {
        const targetDieselLiters = this.metricsData.diesel.power_diesel_target_l
        const reeferActivity = item.staging_reefer_activity
        const dieselOverTargetLiters = reeferActivity.power_diesel_l - targetDieselLiters

        item.diesel_used = this.convertDieselFromLiters(reeferActivity.power_diesel_l, 2)
        this.totalDieselUsed = this.totalDieselUsed + round(item.diesel_used, 1)
        item.staging_duration = Moment.duration(reeferActivity.time_tracked, 'seconds')
        item.over_target = targetDieselLiters === 0 ? 100 : MathUtil.percentage(dieselOverTargetLiters, targetDieselLiters)
        item.fuel_quantity_savings = this.convertDieselFromLiters(dieselOverTargetLiters, 2)
        item.currency_savings = this.sustainabilityParams.dieselPrice * this.convertDieselFromLiters(dieselOverTargetLiters)
        item.co2_savings = this.getCO2Emissions(dieselOverTargetLiters, 2)
        item.power_diesel_pct = reeferActivity.power_diesel_pct
        return item
      })
    },

    /**
     * Load the metrics displayed in the top bar (Also required to get the average diesel usage)
     * @returns {Promise<void>}
     */
    async loadMetrics () {
      this.loadingMetrics = true
      try {
        const metrics = await this.$httpAdapter.get('/sustainability-insights/staging-metrics', {
          params: this.buildMetricsCriteria()
        })
        this.metricsData = first(values(metrics.data))
      } catch (error) {
        this.renderErrorFlashBanner(error)
        throw (error)
      } finally {
        this.loadingMetrics = false
      }
    },

    /**
     * Load the vehicle options for the dropdown
     * @returns {Promise<void>}
     */
    async loadVehicles () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      // Fetch the vehicle options
      try {
        const response = await this.$orm('Vehicle').findAll({
          customer_id: this.selectedCustomer.id,
          sort: 'name',
          'per-page': 0
        })
        this.vehicles = response.getItems()
      } catch (error) {
        this.renderErrorFlashBanner(error)
        throw (error)
      }
    },

    /**
     * Initialize the component
     * @return undefined
     */
    init () {
      this.loadVehicles()
      this.loadMetrics()
    }
  },
  computed: {
    tableControlsMatchDefaults () {
      return isEqual(this.searchCriteria, this.getDefaultSearchCriteria()) &&
        this.remoteData.currentPage === 1 &&
        this.remoteData.sortBy === 'over_target' &&
        this.remoteData.sortDesc === true
    },
    zendeskDomain () {
      return process.env.VUE_APP_ZENDESK_DOMAIN
    },
    /**
     * Get the currently selected customer from route parameters, or use the current customer context.
     */
    selectedCustomer: {
      get () {
        const customerId = parseInt(this.$route.query.customer_id)
        // If no customers specified, default to the current customer context
        if (!Number.isInteger(customerId)) {
          return this.customerContext
        }
        return find(this.customers, x => x.id === customerId)
      }
    },
    /**
     * @return Array
     */
    tableFields () {
      const fields = []

      fields.push({ value: 'vehicle', text: this.$tc('vehicle'), sortable: false })
      fields.push({ value: 'name', text: this.$tc('prior_to_route'), sortable: true })
      fields.push({ value: 'start', text: this.$t('route_start'), sortable: true })
      fields.push({ value: 'staging_duration', text: this.$t('duration'), sortable: true })
      fields.push({ value: 'diesel_used', text: this.$t('diesel_used'), sortable: true })
      fields.push({ value: 'over_target', text: this.$t('percent_over_target'), sortable: true })
      fields.push({ value: 'currency_savings', text: this.sustainabilityParams.currency, sortable: false })
      fields.push({ value: 'fuel_quantity_savings', text: this.$tc(this.dieselUnitLabel, 2), sortable: false })
      fields.push({ value: 'co2_savings', text: this.weightFormatLabel, sortable: false })

      return fields
    },

    vehicleNames () {
      return sortBy(map(this.vehicles, v => {
        return { text: v.name, value: v.id }
      }))
    }
  },
  mounted () {
    this.remoteData.sortBy = 'over_target'
    this.remoteData.sortDesc = true
    this.init()
    this.initiateSearch()
  },
  watch: {
    sustainabilityParams () {
      this.loadMetrics().then(x => {
        this.refreshResults()
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import "src/assets/scss/colors.scss";

</style>
